import {
    AwesomeTableComponent,
    Badge,
    HeaderTable,
    IColumnsProps,
    TabBar,
    TimeUtils,
    useFirstTime,
    ViewLabelStatus,
} from "d-react-components";
import { debounce, filter, reduce } from "lodash";
import { useEffect, useRef, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { JsonParam, useQueryParam } from "use-query-params";
import { ProductStockTransferStatus } from "../../../../api/hooks";
import StockTransferAPI from "../../../../api/queries/stock-transfer";
import AppLink from "../../../../common/AppLink";
import TableAction from "../../../../common/TableActions";
import { STOCK_TRANSFER_STATUSES } from "../../../../constant/stock-transfer";
import Messages from "../../../../languages/Messages";
import Path from "../../../Path";
import StockTransferFilterDrawer from "./StockTransferFilterDrawer";

const FILTER_STOCK_TRANSFER_STATUS = [
    ProductStockTransferStatus.Pending,
    ProductStockTransferStatus.Partial,
    ProductStockTransferStatus.Processing,
    ProductStockTransferStatus.Transferred,
    ProductStockTransferStatus.Completed,
    ProductStockTransferStatus.Cancelled,
];

const STATUS_TABS = [
    {
        id: "all",
        label: "all",
    },
    ...filter(STOCK_TRANSFER_STATUSES, (item) =>
        FILTER_STOCK_TRANSFER_STATUS.includes(item.id)
    ),
];

const StockTransferTable = () => {
    const tableRef = useRef<any>(null);
    const searchRef = useRef<string>("");
    const navigate = useNavigate();
    const [openFilter, setOpenFilter] = useState(false);
    const [summary, setSummary] = useState<any>();
    const [tabSelected, setTabSelected] = useState<any>(STATUS_TABS[0]);
    const [filter, setFilter] = useQueryParam<any>("filter", JsonParam);
    const isFirstTime = useFirstTime();

    useEffect(() => {
        if (isFirstTime) return;
        tableRef.current.refresh();
    }, [filter]);

    useEffect(() => {
        tableRef.current.refresh();
    }, [tabSelected]);

    useEffect(() => {
        // getQuantityOrder();
    }, []);

    const columns: IColumnsProps = [
        {
            title: "ID",
            dataIndex: "transferNo",
            render: (requestNo, item) => (
                <AppLink
                    to={generatePath(Path.INVENTORY_TRANSFER_STOCK_DETAIL, {
                        transferId: item.id,
                    })}
                >
                    {requestNo}
                </AppLink>
            ),
        },
        {
            title: Messages.origin,
            dataIndex: "origin",
            render: (branch) => branch?.name ?? "N/A",
        },
        {
            title: Messages.destination,
            dataIndex: "destination",
            render: (branch) => branch?.name ?? "N/A",
        },
        {
            title: Messages.expectedTransferDate,
            dataIndex: "expectedDateOfTransfer",
            render: (data) => (data ? TimeUtils.toDateTime(data) : "N/A"),
        },
        {
            title: Messages.transferQty,
            dataIndex: "products",
            align: "center",
            render: (products, item) => {
                return reduce(
                    products,
                    (res, item) => res + (item?.stock ?? 0),
                    0
                );
            },
        },
        {
            title: Messages.status,
            dataIndex: "status",
            render: (data) => (
                <ViewLabelStatus
                    status={data}
                    listStatus={STOCK_TRANSFER_STATUSES}
                    getLabel={(item) => (Messages as any)[item.label]}
                />
            ),
        },
        {
            title: Messages.createdAt,
            dataIndex: "createdAt",
            render: (data) => (data ? TimeUtils.toDateTime(data) : "N/A"),
        },
        {
            title: Messages.action,
            dataIndex: "",
            align: "center",
            render: (data: any) => {
                return (
                    <TableAction
                        onClick={(actionId: string) =>
                            onClickTableAction(actionId, data)
                        }
                    />
                );
            },
        },
    ];

    const source = (pagingData: any) => {
        // const filterParam = mapBookingFilterToServer(filter);
        const input = {
            page: pagingData.pageIndex,
            limit: pagingData.pageSize,
            sort: { createdAt: 1 },
            ...(tabSelected?.id !== "all" ? { status: [tabSelected?.id] } : {}),
            // ...filterParam,
        };
        return StockTransferAPI.list(input);
    };

    const onClickTableAction = (actionId: string, data: any) => {
        switch (actionId) {
            default:
                navigate(
                    generatePath(Path.INVENTORY_TRANSFER_STOCK_DETAIL, {
                        transferId: data.id,
                    })
                );
        }
    };

    const onClickAddNew = () => {
        navigate(Path.INVENTORY_TRANSFER_STOCK_CREATE);
    };

    const onChangeSearch = debounce((text) => {
        searchRef.current = text;
        tableRef.current && tableRef.current.refresh();
    }, 500);

    return (
        <div className="">
            <HeaderTable
                onClickNew={onClickAddNew}
                label={Messages.allStockTransfer}
                onClickFilter={() => setOpenFilter(true)}
                onChangeText={(event: any) =>
                    onChangeSearch(event.target.value)
                }
                className="app-layout__page-header"
            />

            <div className="flex">
                <TabBar
                    dataSource={STATUS_TABS}
                    onChange={(tab) => setTabSelected(tab)}
                    value={tabSelected}
                    // className="overflow-x-scroll my-4 pb-1 flex-column form-tabs"
                    getLabel={(item: any) => (
                        <>
                            <span>{Messages[item.label]}</span>
                            {item.id !== "all" &&
                                item.id !==
                                    ProductStockTransferStatus.Cancelled && (
                                    <Badge
                                        index={
                                            summary?.[item.id] > 100
                                                ? "100+"
                                                : summary?.[item.id]
                                        }
                                        variant="index"
                                        size="x-large"
                                        color={
                                            summary?.[item.id] === 0
                                                ? "green"
                                                : "red"
                                        }
                                    />
                                )}
                        </>
                    )}
                    isScroll
                    hideScrollBar={false}
                    minWidthItem="100%"
                    classNameItem="justify-content-start text-gray-500 bg-white"
                    variant="horizontal"
                />
            </div>
            <AwesomeTableComponent
                ref={(ref) => {
                    tableRef.current = ref;
                }}
                source={source}
                columns={columns}
                showSelectColumn
                baseColumnProps={{ width: 100 }}
                getTotalItems={(res) => {
                    return res?.pagination?.items ?? 0;
                }}
                transformer={(res) => res?.data ?? []}
            />
            {openFilter && (
                <StockTransferFilterDrawer
                    open={openFilter}
                    onClose={() => setOpenFilter(false)}
                />
            )}
        </div>
    );
};

export default StockTransferTable;
